import { useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useTownSession } from "../app/TownSessionContext";
import { useTownStore, type RoomProblem } from "../state/store";

/**
 * A room problem with its one useful action: Rejoin when retrying can help, Reload for an outdated tab (ADR-0036:
 * the realtime server refused this build's protocol version, so only fresh code can join), nothing otherwise.
 */
export function RoomProblemBanner({ problem, onRejoin, onReload }: { problem: RoomProblem; onRejoin: (() => void) | null; onReload: () => void }) {
  const outdated = problem.state === "incompatible_client";
  return (
    <div className={`banner ${problem.retryable ? "warning" : "danger"}`} role="alert">
      <span>
        <strong>{problem.title}.</strong> {problem.message}
      </span>
      {outdated ? (
        <button type="button" className="btn btn-sm" onClick={onReload}>
          Reload
        </button>
      ) : problem.retryable && onRejoin ? (
        <button type="button" className="btn btn-sm" onClick={onRejoin}>
          Rejoin
        </button>
      ) : null}
    </div>
  );
}

/**
 * Connection-lost / restarting / degraded notices (docs/01 section 7, docs/06 section 10).
 * The "open in another tab" case has its own dialog; everything else lands here.
 */
export function ConnectionBanner() {
  const session = useTownSession();
  const { status, problem, draining, degraded, transitionPending, sceneStatus } = useTownStore(
    useShallow((s) => ({
      status: s.room.status,
      problem: s.roomProblem,
      draining: s.draining,
      degraded: s.degraded,
      transitionPending: s.ui.transitionPending,
      sceneStatus: s.ui.sceneStatus,
    })),
  );
  const setDegraded = useTownStore((s) => s.setDegraded);
  const [now, setNow] = useState(() => Date.now());
  const [drainStart] = useState(() => Date.now());

  useEffect(() => {
    if (!draining) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [draining]);

  if (sceneStatus !== "ready" && sceneStatus !== "fallback") return null;

  if (draining) {
    const left = Math.max(0, Math.ceil(draining.graceSeconds - (now - drainStart) / 1000));
    return (
      <div className="banner warning" role="status">
        {draining.message || "Townsy is restarting"} — reconnecting in about {left}s
      </div>
    );
  }
  if (problem && problem.state !== "control_taken") {
    return <RoomProblemBanner problem={problem} onRejoin={session ? () => void session.retryJoin() : null} onReload={() => window.location.reload()} />;
  }
  if (status === "reconnecting") {
    return (
      <div className="banner warning" role="status">
        Connection lost — reconnecting…
      </div>
    );
  }
  if (status === "closed" && !problem) {
    return (
      <div className="banner" role="status">
        Not connected to the town.
        {session ? (
          <button type="button" className="btn btn-sm" onClick={() => void session.retryJoin()}>
            Rejoin
          </button>
        ) : null}
      </div>
    );
  }
  if (transitionPending) {
    return (
      <div className="banner" role="status">
        Moving you… (entrance requests are disabled meanwhile)
      </div>
    );
  }
  if (degraded) {
    return (
      <div className="banner warning" role="status">
        {degraded}
        <button type="button" className="btn btn-sm" onClick={() => setDegraded(null)}>
          Dismiss
        </button>
      </div>
    );
  }
  return null;
}
